import type { ComponentType, JSX, MouseEvent as ReactMouseEvent, ReactNode, SVGAttributes } from 'react'
import { forwardRef, useMemo } from 'react'
import type { DropdownMenuItemProps } from '@radix-ui/react-dropdown-menu'

import resolveTailwindClasses from '../utils/resolveTailwindClasses'
import { DropdownItemColor, DropdownItemSize } from './types'

type RadixProps = Pick<DropdownMenuItemProps, 'disabled'>

export interface Props extends RadixProps {
  className?: string
  shortcut?: string
  label?: ReactNode
  LeadingIcon?: ComponentType<SVGAttributes<SVGElement>>
  TrailingIcon?: ComponentType<SVGAttributes<SVGElement>>
  color?: DropdownItemColor
  size?: DropdownItemSize
  onClick?: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void
}

const DropdownItem = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      shortcut,
      label,
      LeadingIcon,
      TrailingIcon,
      disabled,
      color = DropdownItemColor.primary,
      size = DropdownItemSize.medium,
      onClick,
      ...props
    },
    ref
  ): JSX.Element => {
    const colorClasses = useMemo(() => {
      switch (color) {
        case DropdownItemColor.primary:
          return 'text-primary-200 hover:bg-white/4 hover:text-primary-100 focus-visible:bg-white/6 disabled:text-primary-300/50'
        case DropdownItemColor.danger:
          return 'text-error-500 hover:bg-white/4 focus-visible:bg-white/6 disabled:text-error-500/50'
        default:
          console.warn(`Unknown dropdown item color: ${color}`)

          return ''
      }
    }, [color])

    const sizeClasses = useMemo(() => {
      switch (size) {
        case DropdownItemSize.small:
          return 'min-h-8'
        case DropdownItemSize.medium:
          return 'min-h-[42px]'
        default:
          console.warn(`Unknown dropdown item size: ${size}`)

          return ''
      }
    }, [size])

    const shortcutColorClasses = useMemo(() => {
      switch (color) {
        case DropdownItemColor.primary:
          return 'text-primary-300 group-disabled/dropdown-item:text-primary-300/50'
        case DropdownItemColor.danger:
          return 'group-disabled/dropdown-item:text-error-500/50'
        default:
          return ''
      }
    }, [color])

    const shortcutSizeClasses = useMemo(() => {
      switch (size) {
        case DropdownItemSize.small:
          return 'text-xs'
        case DropdownItemSize.medium:
          return 'text-sm'
        default:
          return ''
      }
    }, [size])

    const iconColorClasses = useMemo(() => {
      switch (color) {
        case DropdownItemColor.primary:
          return 'text-primary-300 group-hover/dropdown-item:text-primary-200 group-disabled/dropdown-item:text-primary-300/50'
        case DropdownItemColor.danger:
          return 'group-disabled/dropdown-item:text-error-500/50'
        default:
          return ''
      }
    }, [color])

    const iconSizeClasses = useMemo(() => {
      switch (size) {
        case DropdownItemSize.small:
          return 'size-4'
        case DropdownItemSize.medium:
          return 'size-6'
        default:
          return ''
      }
    }, [size])

    return (
      <button
        ref={ref}
        disabled={disabled}
        {...props}
        className={resolveTailwindClasses(
          'background-transparent group/dropdown-item flex w-full items-center justify-start gap-2 rounded-none px-4 py-1.5 text-left text-sm outline-none disabled:cursor-not-allowed disabled:hover:bg-transparent',
          colorClasses,
          sizeClasses,
          shortcut || TrailingIcon ? 'pe-2.5' : '',
          className
        )}
        onClick={(event) => {
          event.stopPropagation()
          onClick?.(event)
        }}
      >
        {LeadingIcon && <LeadingIcon className={`${iconColorClasses} ${iconSizeClasses}`} />}
        {label}
        {(shortcut || TrailingIcon) && (
          <div className="me-0 ms-auto flex items-center gap-2">
            {shortcut && <span className={`${shortcutColorClasses} ${shortcutSizeClasses}`}>{shortcut}</span>}
            {TrailingIcon && <TrailingIcon className={`${iconColorClasses} ${iconSizeClasses}`} />}
          </div>
        )}
      </button>
    )
  }
)

DropdownItem.displayName = 'DropdownItem'

export default DropdownItem
